import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { API, formatPrice } from "../utils/helpers";
import { Calendar } from "../components/ui/calendar";
import { tr } from "date-fns/locale";
import { format, parse } from "date-fns";
import {
  X, GripHorizontal, LineChart, Coins, BarChart2, CalendarRange, Clock,
  Calendar as CalendarIcon, Search, TrendingUp, TrendingDown, Inbox,
  AlertTriangle, Loader2, ChevronDown
} from "lucide-react";

const HistoryPanel = ({ isOpen, onClose, profile = 'default', theme = 'lacivert' }) => {
  const [activeTab, setActiveTab] = useState('kur');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('daily');
  const [avgMode, setAvgMode] = useState('mesai');
  const [customFrom, setCustomFrom] = useState('09:00');
  const [customTo, setCustomTo] = useState('19:30');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const minuteSelectRef = useRef(null);
  const gosterBtnRef = useRef(null);
  const calendarRef = useRef(null);

  // Drag state
  const [dragPos, setDragPos] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, origX: 0, origY: 0 });
  const panelRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.target.closest('.history-close')) return;
    e.preventDefault();
    const rect = panelRef.current.getBoundingClientRect();
    const startX = dragPos ? dragPos.x : rect.left;
    const startY = dragPos ? dragPos.y : rect.top;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: startX, origY: startY };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setDragPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Reset everything when panel closes
  useEffect(() => {
    if (!isOpen) {
      setDragPos(null);
      setActiveTab('kur');
      setSelectedDate('');
      setSelectedHour('');
      setSelectedMinute('');
      setHistoryData(null);
      setViewMode('daily');
      setAvgMode('mesai');
      setCustomFrom('09:00');
      setCustomTo('19:30');
      setCalendarOpen(false);
    }
  }, [isOpen]);

  // Calculate date range from available dates for calendar navigation limits
  const { fromMonth, toMonth } = useMemo(() => {
    if (availableDates.length === 0) return { fromMonth: undefined, toMonth: undefined };
    const sorted = [...availableDates].sort();
    return {
      fromMonth: parse(sorted[0], 'yyyy-MM-dd', new Date()),
      toMonth: parse(sorted[sorted.length - 1], 'yyyy-MM-dd', new Date()),
    };
  }, [availableDates]);

  useEffect(() => {
    if (calendarOpen && calendarRef.current) {
      setTimeout(() => calendarRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
  }, [calendarOpen]);

  // ---- Premium tema paleti: ozel & modern = LIGHT, digerleri = DARK ----
  const accentMap = { yesil: '#FFA93E', lacivert: '#FFA93E', ozel: '#FFA93E', mavi: '#FFA93E', koyumavi: '#FFA93E', modern: '#C8860A' };
  const accent = accentMap[theme] || '#FFA93E';
  const isLight = theme === 'ozel' || theme === 'modern';
  const ui = isLight
    ? {
        surface: '#ffffff', elevated: '#F4F1EB', border: 'rgba(0,0,0,0.07)',
        textPrimary: '#1f2937', textSecondary: '#6b7280', backdrop: 'rgba(15,23,42,0.28)',
        rowHover: 'rgba(0,0,0,0.035)', inputBg: '#ffffff', shadow: '0 24px 60px -12px rgba(0,0,0,0.28)'
      }
    : {
        surface: '#0f172a', elevated: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.10)',
        textPrimary: '#f8fafc', textSecondary: '#94a3b8', backdrop: 'rgba(0,0,0,0.62)',
        rowHover: 'rgba(255,255,255,0.06)', inputBg: 'rgba(255,255,255,0.04)', shadow: '0 24px 60px -12px rgba(0,0,0,0.6)'
      };

  const hours = useMemo(() => {
    const uniqueHours = [...new Set(availableTimes.map(t => t.split(':')[0]))];
    return uniqueHours.sort();
  }, [availableTimes]);

  const minutes = useMemo(() => {
    if (!selectedHour) return [];
    const filteredMinutes = availableTimes
      .filter(t => t.startsWith(selectedHour + ':'))
      .map(t => t.split(':')[1]);
    return [...new Set(filteredMinutes)].sort();
  }, [availableTimes, selectedHour]);

  useEffect(() => {
    if (isOpen) loadDates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, profile]);

  useEffect(() => {
    if (selectedDate && (activeTab === 'kur' || activeTab === 'ons') && viewMode === 'minute') loadTimes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, activeTab, viewMode, profile]);

  useEffect(() => { setSelectedMinute(''); }, [selectedHour]);

  const loadDates = async () => {
    try {
      let endpoint;
      if (activeTab === 'kur') {
        endpoint = `${API}/history/prices/dates?profile=${profile}`;
      } else if (activeTab === 'ons') {
        endpoint = `${API}/history/ons-gold/dates`;
      } else {
        endpoint = `${API}/history/sarrafiye-has/dates`;
      }
      const res = await axios.get(endpoint);
      setAvailableDates(res.data.dates || []);
      if (res.data.dates?.length > 0) setSelectedDate(res.data.dates[0]);
    } catch (e) { console.error("Tarih yukleme hatasi:", e); }
  };

  const loadTimes = async () => {
    try {
      const endpoint = activeTab === 'ons'
        ? `${API}/history/ons-gold/times?date=${selectedDate}`
        : `${API}/history/prices/times?date=${selectedDate}&profile=${profile}`;
      const res = await axios.get(endpoint);
      setAvailableTimes(res.data.times || []);
      setSelectedHour('');
      setSelectedMinute('');
    } catch (e) { console.error("Saat yukleme hatasi:", e); }
  };

  const loadHistory = async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      let url;
      const avgParams = viewMode === 'daily' ? `&avg_mode=${avgMode}${avgMode === 'custom' ? `&time_from=${customFrom}&time_to=${customTo}` : ''}` : '';

      if (activeTab === 'kur') {
        const selectedTime = selectedHour && selectedMinute ? `${selectedHour}:${selectedMinute}` : '';
        url = viewMode === 'minute' && selectedTime
          ? `${API}/history/prices?date=${selectedDate}&time=${selectedTime}&profile=${profile}`
          : `${API}/history/prices?date=${selectedDate}&profile=${profile}${avgParams}`;
      } else if (activeTab === 'ons') {
        const selectedTime = selectedHour && selectedMinute ? `${selectedHour}:${selectedMinute}` : '';
        url = viewMode === 'minute' && selectedTime
          ? `${API}/history/ons-gold?date=${selectedDate}&time=${selectedTime}`
          : `${API}/history/ons-gold?date=${selectedDate}${avgParams}`;
      } else {
        url = `${API}/history/sarrafiye-has?date=${selectedDate}`;
      }
      const res = await axios.get(url);
      setHistoryData(res.data);
    } catch (e) {
      console.error("Gecmis yukleme hatasi:", e);
      setHistoryData({ error: "Veri yuklenemedi" });
    } finally { setLoading(false); }
  };

  const getProfileName = () => {
    if (profile === '1') return 'UFUKTEPE';
    if (profile === '2') return 'DUTLUK';
    return 'ANA PROFİL';
  };

  const avgLabel = avgMode === '24h' ? '24 Saat' : avgMode === 'mesai' ? '09:00 - 19:30' : `${customFrom} - ${customTo}`;

  const sortedDates = [...availableDates].sort();
  const dateMin = sortedDates[0];
  const dateMax = sortedDates[sortedDates.length - 1];

  if (!isOpen) return null;

  // ---- Stil yardimcilari ----
  const tabs = [
    { id: 'kur', label: 'Kur Geçmişi', Icon: LineChart },
    { id: 'sarrafiye', label: 'Sarrafiye Has', Icon: Coins },
    { id: 'ons', label: 'ONS Gold', Icon: BarChart2 },
  ];

  const segWrap = {
    display: 'flex', gap: 4, padding: 4, borderRadius: 12,
    background: ui.elevated, border: `1px solid ${ui.border}`,
  };
  const segBtn = (active) => ({
    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
    padding: '9px 12px', borderRadius: 9, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, letterSpacing: '0.01em',
    transition: 'all 0.18s ease',
    background: active ? ui.surface : 'transparent',
    color: active ? accent : ui.textSecondary,
    boxShadow: active ? (isLight ? '0 1px 3px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.4)') : 'none',
  });
  const pill = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 16px', borderRadius: 999, cursor: 'pointer',
    fontSize: 12.5, fontWeight: active ? 700 : 500,
    border: `1px solid ${active ? accent : ui.border}`,
    background: active ? `${accent}1A` : 'transparent',
    color: active ? accent : ui.textSecondary,
    transition: 'all 0.18s ease',
  });
  const inputStyle = {
    padding: '9px 12px', fontSize: 13.5, borderRadius: 10,
    border: `1px solid ${ui.border}`, background: ui.inputBg, color: ui.textPrimary, outline: 'none',
  };

  const TableWrap = ({ children }) => (
    <div style={{ borderRadius: 14, border: `1px solid ${ui.border}`, overflow: 'hidden', background: ui.surface }}>
      <table className="hp-table">{children}</table>
    </div>
  );

  const Th = ({ children, right }) => (
    <th style={{
      padding: '11px 14px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.09em', color: ui.textSecondary, textAlign: right ? 'right' : 'left',
      background: ui.elevated, borderBottom: `1px solid ${ui.border}`, whiteSpace: 'nowrap',
    }}>{children}</th>
  );

  const renderPriceRows = (rows, testidPrefix) => rows.map((r, i) => (
    <tr key={i} className="hp-row" data-testid={`${testidPrefix}-row-${i}`}>
      <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, color: ui.textSecondary, borderTop: `1px solid ${ui.border}` }}>{r.label}</td>
      <td style={{ padding: '10px 14px', fontSize: 13.5, fontWeight: 600, color: ui.textPrimary, textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderTop: `1px solid ${ui.border}` }}>{r.alis}</td>
      <td style={{ padding: '10px 14px', fontSize: 13.5, fontWeight: 600, color: ui.textPrimary, textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderTop: `1px solid ${ui.border}` }}>{r.satis}</td>
    </tr>
  ));

  const MinMaxCard = ({ label, max, min, Icon }) => (
    <div style={{
      flex: 1, position: 'relative', overflow: 'hidden', background: ui.elevated,
      border: `1px solid ${ui.border}`, borderRadius: 14, padding: '12px 14px',
    }}>
      <div style={{ position: 'absolute', right: -16, top: -16, width: 60, height: 60, borderRadius: 999, background: `${accent}22`, filter: 'blur(14px)', pointerEvents: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: ui.textSecondary }}>
        <Icon size={13} /> {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 8 }}>
        <span style={{ fontSize: 10, color: ui.textSecondary }}>En Yüksek</span>
        <span style={{ fontSize: 16, fontWeight: 800, color: accent, fontVariantNumeric: 'tabular-nums' }}>{max}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontSize: 10, color: ui.textSecondary }}>En Düşük</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: ui.textPrimary, fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>{min}</span>
      </div>
    </div>
  );

  const sectionTitle = (txt) => (
    <h3 style={{ margin: '0 0 12px', textAlign: 'center', fontSize: 14, fontWeight: 600, letterSpacing: '0.01em', color: ui.textPrimary }}>{txt}</h3>
  );

  return (
    <div
      className="history-overlay"
      onClick={dragPos ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: ui.backdrop, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
        overflowY: 'auto', padding: 16,
      }}
    >
      <style>{`
        .hp-row { transition: background 0.15s ease; }
        .hp-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .hp-table td:first-child, .hp-table th:first-child { width: 40%; }
        .hp-row:hover td { background: ${ui.rowHover}; }
        .hp-scroll::-webkit-scrollbar { width: 8px; }
        .hp-scroll::-webkit-scrollbar-thumb { background: ${ui.border}; border-radius: 8px; }
        .hp-scroll::-webkit-scrollbar-track { background: transparent; }
        @keyframes hp-spin { to { transform: rotate(360deg); } }
        .hp-spin { animation: hp-spin 0.8s linear infinite; }
        .hp-select { appearance: none; -webkit-appearance: none; }
      `}</style>

      <div
        ref={panelRef}
        className="hp-scroll"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 'min(640px, 95vw)', maxHeight: '88vh', marginTop: '4vh',
          display: 'flex', flexDirection: 'column', overflowY: 'auto',
          background: ui.surface, color: ui.textPrimary,
          borderRadius: 20, border: `1px solid ${ui.border}`, boxShadow: ui.shadow,
          fontFamily: "'Outfit', system-ui, -apple-system, sans-serif",
          ...(dragPos ? { position: 'fixed', left: dragPos.x, top: dragPos.y, margin: 0, zIndex: 10000, transition: isDragging ? 'none' : undefined } : {}),
        }}
        data-testid="history-panel"
      >
        {/* HEADER */}
        <div
          onMouseDown={handleMouseDown}
          data-testid="history-header-drag"
          style={{
            position: 'sticky', top: 0, zIndex: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', background: ui.surface,
            borderBottom: `1px solid ${ui.border}`,
            cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none',
          }}
        >
          {/* gold divider glow */}
          <div style={{ position: 'absolute', bottom: -1, left: 0, width: '100%', height: 1, background: `linear-gradient(90deg, transparent, ${accent}55, transparent)` }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <GripHorizontal size={16} style={{ color: ui.textSecondary, opacity: 0.6 }} />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', color: ui.textPrimary }}>Geçmiş Veriler</h2>
            <span style={{ padding: '3px 9px', borderRadius: 7, background: `${accent}1A`, color: accent, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em' }}>
              {getProfileName()}
            </span>
          </div>
          <button
            className="history-close"
            onClick={onClose}
            data-testid="history-modal-close"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 999, border: 'none', cursor: 'pointer',
              background: 'transparent', color: ui.textSecondary, transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.color = '#ef4444'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = ui.textSecondary; }}
          >
            <X size={18} />
          </button>
        </div>

        {/* TABS */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${ui.border}`, padding: '0 12px', background: ui.surface }}>
          {tabs.map(({ id, label, Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => { setActiveTab(id); setHistoryData(null); setCalendarOpen(false); }}
                data-testid={`tab-${id}`}
                style={{
                  flex: 1, position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  padding: '14px 6px', border: 'none', background: 'transparent', cursor: 'pointer',
                  fontSize: 13.5, fontWeight: active ? 700 : 500,
                  color: active ? accent : ui.textSecondary, transition: 'color 0.18s ease',
                }}
              >
                <Icon size={15} /> {label}
                {active && (
                  <span style={{ position: 'absolute', bottom: -1, left: '12%', width: '76%', height: 2.5, background: accent, borderRadius: '4px 4px 0 0', boxShadow: `0 -1px 10px ${accent}88` }} />
                )}
              </button>
            );
          })}
        </div>

        {/* CONTROLS */}
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14, background: isLight ? '#FBFAF7' : 'rgba(255,255,255,0.015)' }}>
          {(activeTab === 'kur' || activeTab === 'ons') && (
            <div style={segWrap}>
              <button onClick={() => { setViewMode('daily'); setSelectedHour(''); setSelectedMinute(''); setCalendarOpen(false); }} style={segBtn(viewMode === 'daily')} data-testid="mode-daily">
                <CalendarRange size={15} /> Günlük Ortalama
              </button>
              <button onClick={() => { setViewMode('minute'); setCalendarOpen(false); }} style={segBtn(viewMode === 'minute')} data-testid="mode-minute">
                <Clock size={15} /> Dakikalık
              </button>
            </div>
          )}

          {(activeTab === 'kur' || activeTab === 'ons') && viewMode === 'daily' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: 'center' }}>
                {[{ key: '24h', label: '24 Saat' }, { key: 'mesai', label: 'Mesai Saatleri' }, { key: 'custom', label: 'Zaman Aralığı' }].map(opt => (
                  <button key={opt.key} onClick={() => setAvgMode(opt.key)} style={pill(avgMode === opt.key)} data-testid={`avg-mode-${opt.key}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {avgMode === 'custom' && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="time" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={inputStyle} data-testid="custom-time-from" />
                  <span style={{ color: ui.textSecondary }}>—</span>
                  <input type="time" value={customTo} onChange={e => setCustomTo(e.target.value)} style={inputStyle} data-testid="custom-time-to" />
                </div>
              )}
            </div>
          )}

          {/* Tarih + (dakikalik) saatler tek satir */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: ui.textSecondary }}>Tarih</label>
              <input
                type="date"
                value={selectedDate}
                min={dateMin}
                max={dateMax}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedHour('');
                  setSelectedMinute('');
                }}
                data-testid="date-picker-input"
                style={{ ...inputStyle, minWidth: 150, cursor: 'pointer', colorScheme: isLight ? 'light' : 'dark' }}
              />
            </div>

            {(activeTab === 'kur' || activeTab === 'ons') && viewMode === 'minute' && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: ui.textSecondary }}>Saat</label>
                  <select className="hp-select" value={selectedHour} onChange={(e) => setSelectedHour(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && minuteSelectRef.current) { e.preventDefault(); minuteSelectRef.current.focus(); } }}
                    style={{ ...inputStyle, minWidth: 90, cursor: 'pointer' }}>
                    <option value="">Seç</option>
                    {hours.map(h => (<option key={h} value={h}>{h}</option>))}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: ui.textSecondary }}>Dakika</label>
                  <select className="hp-select" ref={minuteSelectRef} value={selectedMinute} onChange={(e) => setSelectedMinute(e.target.value)} disabled={!selectedHour}
                    onKeyDown={(e) => { if (e.key === 'Enter' && gosterBtnRef.current) { e.preventDefault(); gosterBtnRef.current.focus(); gosterBtnRef.current.click(); } }}
                    style={{ ...inputStyle, minWidth: 90, cursor: 'pointer', opacity: selectedHour ? 1 : 0.5 }}>
                    <option value="">Seç</option>
                    {minutes.map(m => (<option key={m} value={m}>{m}</option>))}
                  </select>
                </div>
              </>
            )}
          </div>

          <button
            ref={gosterBtnRef}
            onClick={loadHistory}
            disabled={loading}
            data-testid="button-goster"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '12px 18px', borderRadius: 11, border: 'none', cursor: loading ? 'wait' : 'pointer',
              background: `linear-gradient(135deg, ${theme === 'modern' ? '#C8860A' : '#C8860A'}, ${accent})`,
              color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: '0.01em',
              boxShadow: `0 4px 14px ${accent}66`, transition: 'all 0.18s ease', opacity: loading ? 0.85 : 1,
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'scale(1.015)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {loading ? <Loader2 size={16} className="hp-spin" /> : <Search size={16} />}
            {loading ? 'Yükleniyor...' : 'Göster'}
          </button>
        </div>

        {/* CONTENT */}
        <div style={{ padding: 18 }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: 12 }}>
              <Loader2 size={30} className="hp-spin" style={{ color: accent }} />
              <span style={{ fontSize: 13, color: ui.textSecondary }}>Veriler yükleniyor...</span>
            </div>
          )}

          {!loading && historyData && !historyData.error && activeTab === 'kur' && (
            <div>
              {sectionTitle(viewMode === 'daily' ? `${selectedDate} — Günlük Ortalama (${avgLabel})` : `${selectedDate} — ${selectedHour}:${selectedMinute}`)}
              <TableWrap>
                <thead><tr><Th>Ürün</Th><Th right>QEN Alış</Th><Th right>QEN Satış</Th></tr></thead>
                <tbody>
                  {renderPriceRows(viewMode === 'minute' ? [
                    { label: 'Has Altın', alis: formatPrice(historyData.has_alis), satis: formatPrice(historyData.has_satis) },
                    { label: '1 Gram', alis: formatPrice(historyData.gram_1_alis), satis: formatPrice(historyData.gram_1_satis) },
                    { label: '5 Gram', alis: formatPrice(historyData.gram_5_alis), satis: formatPrice(historyData.gram_5_satis) },
                    { label: '10 Gram', alis: formatPrice(historyData.gram_10_alis), satis: formatPrice(historyData.gram_10_satis) },
                    { label: '20 Gram', alis: formatPrice(historyData.gram_20_alis), satis: formatPrice(historyData.gram_20_satis) },
                    { label: '22 Ayar', alis: formatPrice(historyData.bilezik_alis), satis: '-' },
                    { label: 'Çeyrek', alis: formatPrice(historyData.ceyrek_alis), satis: formatPrice(historyData.ceyrek_satis) },
                    { label: 'Yarım', alis: formatPrice(historyData.yarim_alis), satis: formatPrice(historyData.yarim_satis) },
                    { label: 'Teklik', alis: formatPrice(historyData.teklik_alis), satis: formatPrice(historyData.teklik_satis) },
                    { label: 'Ata Lira', alis: formatPrice(historyData.ata_lira_alis), satis: formatPrice(historyData.ata_lira_satis) },
                    { label: 'Gremse', alis: formatPrice(historyData.gremse_alis), satis: formatPrice(historyData.gremse_satis) },
                  ] : [
                    { label: 'Has Altın', alis: formatPrice(historyData.has_altin_qen_alis), satis: formatPrice(historyData.has_altin_qen_satis) },
                    { label: '1 Gram', alis: formatPrice(historyData.gram_1_qen_alis), satis: formatPrice(historyData.gram_1_qen_satis) },
                    { label: '5 Gram', alis: formatPrice(historyData.gram_5_qen_alis), satis: formatPrice(historyData.gram_5_qen_satis) },
                    { label: '10 Gram', alis: formatPrice(historyData.gram_10_qen_alis), satis: formatPrice(historyData.gram_10_qen_satis) },
                    { label: '20 Gram', alis: formatPrice(historyData.gram_20_qen_alis), satis: formatPrice(historyData.gram_20_qen_satis) },
                    { label: '22 Ayar', alis: formatPrice(historyData.bilezik_qen_alis), satis: '-' },
                    { label: 'Çeyrek', alis: formatPrice(historyData.ceyrek_qen_alis), satis: formatPrice(historyData.ceyrek_qen_satis) },
                    { label: 'Yarım', alis: formatPrice(historyData.yarim_qen_alis), satis: formatPrice(historyData.yarim_qen_satis) },
                    { label: 'Teklik', alis: formatPrice(historyData.teklik_qen_alis), satis: formatPrice(historyData.teklik_qen_satis) },
                    { label: 'Ata Lira', alis: formatPrice(historyData.ata_lira_qen_alis), satis: formatPrice(historyData.ata_lira_qen_satis) },
                    { label: 'Gremse', alis: formatPrice(historyData.gremse_qen_alis), satis: formatPrice(historyData.gremse_qen_satis) },
                  ], 'kur')}
                </tbody>
              </TableWrap>

              {viewMode === 'daily' && (
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <MinMaxCard label="Has Alış" max={formatPrice(historyData.has_alis_max)} min={formatPrice(historyData.has_alis_min)} Icon={TrendingUp} />
                  <MinMaxCard label="Has Satış" max={formatPrice(historyData.has_satis_max)} min={formatPrice(historyData.has_satis_min)} Icon={TrendingDown} />
                </div>
              )}
              {viewMode === 'daily' && historyData.record_count && (
                <p style={{ textAlign: 'center', color: ui.textSecondary, marginTop: 12, fontSize: 12 }}>{avgLabel} arası {historyData.record_count} kayıttan hesaplandı</p>
              )}
            </div>
          )}

          {!loading && historyData && !historyData.error && activeTab === 'sarrafiye' && (() => {
            const src = historyData.average || historyData.records?.[0]?.data;
            const rows = src ? [
              { label: 'Çeyrek', d: src.ceyrek }, { label: 'Yarım', d: src.yarim }, { label: 'Teklik', d: src.teklik },
              { label: 'Ata Lira', d: src.ata }, { label: 'Gremse', d: src.gremse },
              { label: 'Yeni Çeyrek', d: src.yeni_ceyrek }, { label: 'Yeni Yarım', d: src.yeni_yarim },
              { label: 'Yeni Teklik', d: src.yeni_teklik }, { label: 'Yeni Ata Lira', d: src.yeni_ata }, { label: 'Yeni Gremse', d: src.yeni_gremse },
            ] : [];
            return (
              <div>
                {sectionTitle(`${selectedDate} — Sarrafiye Has`)}
                <TableWrap>
                  <thead><tr><Th>Ürün</Th><Th right>Has Alış</Th><Th right>Has Satış</Th></tr></thead>
                  <tbody>{renderPriceRows(rows.map(r => ({ label: r.label, alis: r.d?.alis || '-', satis: r.d?.satis || '-' })), 'sarrafiye')}</tbody>
                </TableWrap>
              </div>
            );
          })()}

          {!loading && historyData && !historyData.error && activeTab === 'ons' && (
            <div>
              {sectionTitle(viewMode === 'daily' ? `${selectedDate} — ONS Gold (${avgLabel})` : `${selectedDate} — ${selectedHour}:${selectedMinute} ONS Gold`)}
              <TableWrap>
                <thead><tr><Th>Ürün</Th><Th right>Alış</Th><Th right>Satış</Th></tr></thead>
                <tbody>
                  {renderPriceRows(viewMode === 'minute' ? [
                    { label: 'ONS Altın', alis: historyData.alis, satis: historyData.satis },
                    ...(historyData.has_alis ? [{ label: 'Has Altın', alis: formatPrice(historyData.has_alis), satis: formatPrice(historyData.has_satis) }] : []),
                  ] : [
                    { label: 'ONS Altın', alis: historyData.alis_avg, satis: historyData.satis_avg },
                    ...(historyData.has_alis_avg ? [{ label: 'Has Altın', alis: formatPrice(historyData.has_alis_avg), satis: formatPrice(historyData.has_satis_avg) }] : []),
                  ], 'ons')}
                </tbody>
              </TableWrap>

              {viewMode === 'daily' && (
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <MinMaxCard label="Alış" max={historyData.alis_max} min={historyData.alis_min} Icon={TrendingUp} />
                  <MinMaxCard label="Satış" max={historyData.satis_max} min={historyData.satis_min} Icon={TrendingDown} />
                </div>
              )}
              {viewMode === 'daily' && historyData.record_count && (
                <p style={{ textAlign: 'center', color: ui.textSecondary, marginTop: 12, fontSize: 12 }}>{avgLabel} arası {historyData.record_count} kayıttan hesaplandı</p>
              )}
            </div>
          )}

          {!loading && historyData?.error && (
            <div data-testid="history-error" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderRadius: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', fontSize: 13.5 }}>
              <AlertTriangle size={18} /> {historyData.error}
            </div>
          )}

          {!loading && !historyData && (
            <div data-testid="history-empty" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '44px 0', textAlign: 'center', gap: 14 }}>
              <Inbox size={46} strokeWidth={1.4} style={{ color: ui.textSecondary, opacity: 0.4 }} />
              <span style={{ fontSize: 13.5, color: ui.textSecondary, maxWidth: 240 }}>Tarih seçip <b style={{ color: accent }}>Göster</b> butonuna basın</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryPanel;
